import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fiscalMonthLabel } from "@/lib/format";

const ALL = "__all__";

export function useFilterOptions() {
  const [fiscal, setFiscal] = useState({ fiscal_year_start_month: 1 });
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [years, setYears] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [f, d, u, s] = await Promise.all([
          api.get("/settings/fiscal"),
          api.get("/departments"),
          api.get("/users"),
          api.get("/dashboard/summary"),
        ]);
        setFiscal(f.data);
        setDepartments(d.data);
        setUsers(u.data);
        const yrs = s.data.fiscal_years || [];
        if (!yrs.includes(f.data.current_fiscal_year) && f.data.current_fiscal_year) yrs.unshift(f.data.current_fiscal_year);
        setYears(yrs);
      } catch (_) {}
    })();
  }, []);

  return { fiscal, departments, users, years };
}

export default function FilterBar({ filters, setFilters, showPeriod = true, showUser = true }) {
  const { user } = useAuth();
  const { fiscal, departments, users, years } = useFilterOptions();
  const startMonth = fiscal.fiscal_year_start_month || 1;
  const set = (k, v) => setFilters((prev) => ({ ...prev, [k]: v === ALL ? "" : v }));

  const filteredUsers = filters.department_id
    ? users.filter((u) => u.department_id === filters.department_id)
    : users;

  const canFilterPeople = user?.role === "super_admin" || user?.role === "manager";

  return (
    <div className="flex flex-wrap items-end gap-3 border border-border bg-card p-4" data-testid="filter-bar">
      <div className="space-y-1">
        <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold">Fiscal Year</div>
        <Select value={filters.fiscal_year || ALL} onValueChange={(v) => set("fiscal_year", v)}>
          <SelectTrigger className="w-[150px] rounded-none h-9" data-testid="filter-fiscal-year"><SelectValue placeholder="All years" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All years</SelectItem>
            {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {showPeriod && (
        <>
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold">Half</div>
            <Select value={filters.half || ALL} onValueChange={(v) => set("half", v)}>
              <SelectTrigger className="w-[120px] rounded-none h-9" data-testid="filter-half"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All</SelectItem>
                <SelectItem value="1">H1</SelectItem>
                <SelectItem value="2">H2</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold">Quarter</div>
            <Select value={filters.quarter || ALL} onValueChange={(v) => set("quarter", v)}>
              <SelectTrigger className="w-[120px] rounded-none h-9" data-testid="filter-quarter"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All</SelectItem>
                {[1, 2, 3, 4].map((q) => <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold">Month</div>
            <Select value={filters.month || ALL} onValueChange={(v) => set("month", v)}>
              <SelectTrigger className="w-[120px] rounded-none h-9" data-testid="filter-month"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All</SelectItem>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                  <SelectItem key={m} value={String(m)}>{fiscalMonthLabel(m, startMonth)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {canFilterPeople && (
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold">Department</div>
          <Select value={filters.department_id || ALL} onValueChange={(v) => { set("department_id", v); set("user_id", ALL); }}>
            <SelectTrigger className="w-[170px] rounded-none h-9" data-testid="filter-department"><SelectValue placeholder="All departments" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All departments</SelectItem>
              {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {canFilterPeople && showUser && (
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold">User</div>
          <Select value={filters.user_id || ALL} onValueChange={(v) => set("user_id", v)}>
            <SelectTrigger className="w-[180px] rounded-none h-9" data-testid="filter-user"><SelectValue placeholder="All users" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All users</SelectItem>
              {filteredUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
